import { type LayerTemplate } from '../Pages/MapPage/@types'
import { action, makeAutoObservable, reaction, toJS } from 'mobx'
import { type RGBAColor } from '@deck.gl/core'
import type PickInfo from '@deck.gl/core/typed/lib/deck'
import { TileLayer } from '@deck.gl/geo-layers'
import {
  BitmapLayer,
  GeoJsonLayer,
  PolygonLayer,
  ScatterplotLayer,
  IconLayer
} from '@deck.gl/layers'
import { load } from '@loaders.gl/core'
import { ImageLoader } from '@loaders.gl/images'
import BuildingsimulationLayer from '../Pages/MapPage/Layers/BuildingSimulationLayer'
import EnergyPlantLayer from '../Pages/MapPage/Layers/EnergyPlantLayer'
import QuarterLayer from '../Pages/MapPage/Layers/QuarterLayer'
import { type ObjectData } from '../Pages/MapPage/Monitor/@types'
import EnekaDefinitions from '../preset.json'
import { apiCall } from '../Api'
import { isMobile } from 'react-device-detect'
import iconAtlas from '../assets/images/icons/icon-atlas.png'
import InsertedQuarterLayer from '../Pages/MapPage/Layers/InsertedQuarterLayer'
import WMSLayer from '../Pages/MapPage/Layers/WMSLayer'

import VegetationSimulationLayer from '../Pages/MapPage/Layers/VegetationSimulationLayer'

import type AppStore from './AppStore'
import { FeatureType } from './features'
import AggregationLayer from 'Pages/MapPage/Layers/AggregationLayer'
import type ObjectInfo from '@deck.gl/core/typed/lib/layer'

const ICON_MAPPING = {
  marker: {
    x: 0,
    y: 0,
    width: 128,
    height: 128,
    mask: true
  }
}

const UNDEFINED_LINE_COLOR = [192, 192, 192, 80]
const UNDEFINED_FILL_COLOR = [192, 192, 192, 20]
const DEFAULT_LINE_WIDTH = 4
const DEFAULT_COLOR_ALPHA = 178
const PREDOMINANT_VALUE_FIELD = 'predominant_value'

interface LayerListElement {
  visible: boolean
  init: boolean
  version?: number
}

class LayerStore {
  public layerList: LayerTemplate[] = []
  public themeTree = EnekaDefinitions.themes
  public fitBounds: any = null

  public reportLayer: any[] = []
  public hoverInfo: any = {}
  public lightingEffect: any = {}
  public layersRendered: boolean = false
  public buildingLayerList: Record<string, LayerListElement> = {
    // layer handling list
    quarter: { visible: true, version: 1, init: true },
    drawQuarter: { visible: false, init: false },
    drawNewQuarter: { visible: false, init: false },
    vegetationsimulation: { visible: false, version: 1, init: false },
    buildingsimulation: { visible: false, version: 1, init: false },
    marker: { visible: false, init: false },
    heatmap: { visible: false, init: false },
    energyplant: { visible: false, init: false },
    solartile: { visible: false, init: false },
    geothermalLayer: { visible: false, init: false },
    custom: { visible: false, init: false },
    drawConsumption: { visible: false, init: false },
    IconSelect: { visible: false, init: false },
    addressLayer: { visible: true, init: true },
    heatPotential: { visible: false, init: true }
  }

  public aggregationLayers: any[] = []
  public selectedAggregationLayer: any = {
    id: 'buildings',
    layer: {},
    internal: true,
    line_width: DEFAULT_LINE_WIDTH
  }

  constructor(private readonly appStore: AppStore) {
    makeAutoObservable(this, {
      handleWeight: false,
      themeTree: false
    })
    this.appStore = appStore

    void this.loadAggregationLayers()

    reaction(
      () => ({ layers: toJS(this.buildingLayerList) }),
      ({ layers }) => {
        const keys = Object.keys(layers)
        keys.forEach((key) => {
          if (!layers[key].init && layers[key].visible) {
            this.buildingLayerList[key].init = true
          }
        })
      },
      { delay: 150 }
    )
  }

  addCustomLayer(service: string, layer: string, version: string) {
    const delimiter = this.getUrlParameterDelimiter(service)
    this.buildingLayerList.custom = {
      ...this.buildingLayerList.custom,
      visible: true,
      url: service + delimiter + 'layers=' + layer,
      service,
      layer,
      version
    }
  }

  removeCustomLayer() {
    this.buildingLayerList.custom = {
      visible: false,
      init: true,
      url: '',
      version: ''
    }
  }

  // Heatmap weight calc
  handleWeight = (properties, subTheme, apivariable) => {
    const apivariableArray = apivariable.split(',')
    switch (subTheme.replace('_', ' - ')) {
      case 'Gesamt - Emissionen':
      case 'Gesamt - Energiekosten':
        return (
          properties[apivariableArray[0].trim()] +
          properties[apivariableArray[1].trim()]
        )
      case 'Eigenbedarf - Geothermie':
        return (
          properties[apivariableArray[0].trim()] /
          properties[apivariableArray[1].trim()]
        )
      case 'Eigenbedarf - Photovoltaik':
      case 'Eigenbedarf - Solarthermie':
        return (
          (properties[apivariableArray[0].trim()] /
            properties[apivariableArray[1].trim()]) *
          100
        )
      case 'Sanierungspotenzial':
        return (
          ((properties[apivariableArray[1].trim()] -
            properties[apivariableArray[0].trim()]) /
            (properties[apivariableArray[1].trim()] > 0
              ? properties[apivariableArray[1].trim()]
              : 1)) *
          100
        )
      case 'Wärmebedarf':
      case 'Strombedarf':
        return (
          properties[apivariableArray[0].trim()] /
          properties[apivariableArray[1].trim()]
        )
      case 'Fern-/Nahwärmepotenzial':
        return properties[apivariableArray[0].trim()] <= 150
          ? 0
          : properties[apivariable]
      default:
        return properties[apivariable]
    }
  }

  // Modal handling
  openReportModal() {
    this.getAvailableReports(
      this.appStore.focusIds[0],
      this.appStore.focusType
    ).then((res) => {
      this.appStore.monitorStore.showReportModal(res)
    })
  }

  openFormModal() {
    this.getAvailableForms(
      this.appStore.focusIds[0],
      EnekaDefinitions.object['Anträge'].typemap[this.appStore.focusType ?? '']
        .context
    ).then((res) => {
      this.appStore.monitorStore.showFormModal(res)
    })
  }

  // Getter and Setter
  setHistoryObjectSelectionType(object: ObjectData): void {
    if (!this.appStore.appStore.currentHistoryObject) return
    this.appStore.appStore.currentHistoryObject.object_uuid =
      object.properties?.uuid
    this.appStore.appStore.currentHistoryObject.selection_type =
      object.layerType
    if (!this.appStore.appStore.currentHistoryObject.title) return
    switch (this.appStore.appStore.currentHistoryObject.action) {
      case 'report': {
        this.openReportModal()
        return
      }
      case 'support_application': {
        this.openFormModal()
        return
      }
      case 'print':
        this.appStore.monitorStore.modalReportOpen = false
        this.appStore.monitorStore.modalFormOpen = false
        this.appStore.monitorStore.modalPrintOpen = true
    }
  }

  setReportLayer(reportLayer: any[]): void {
    this.reportLayer = reportLayer
  }

  setLayersRendered(layersRendered: boolean): void {
    this.layersRendered = layersRendered
  }

  setHoverInfo(hoverInfo): void {
    this.hoverInfo = hoverInfo
  }

  setDrawNewQuarter(drawNewQuarter: {
    visible: boolean
    data: any
    color: string
  }) {
    this.buildingLayerList.drawNewQuarter = drawNewQuarter
  }

  setLayerVisibility(layer: LayerListElement, visible: boolean): void {
    this.setLayerAttribute(layer, 'visible', visible)
  }

  setLayerAttribute(layer: LayerListElement, attr: string, value: any): void {
    layer[attr] = value
  }

  async getAvailableReports(id, old_context: FeatureType | null) {
    if (old_context === null) return []
    const context = ['building', 'vegetation', 'energyplant'].includes(
      old_context
    )
      ? old_context + 'simulation'
      : old_context
    return await apiCall({
      url:
        context === FeatureType.Project
          ? `/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/report/`
          : `/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/${context}/${id}/report/`,
      method: 'GET',
      accessToken: this.appStore.userStore.token?.access
    }).then((res) => {
      return res.filter((entry) => !entry.slug.includes('vorhabenbeschreibung'))
    })
  }

  async getAvailableForms(id, old_context) {
    const context = ['building', 'vegetation', 'energyplant'].includes(
      old_context
    )
      ? old_context + 'simulation'
      : old_context
    return await apiCall({
      url:
        context === FeatureType.Project
          ? `/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/report/`
          : `/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/${context}/${id}/report/`,
      method: 'GET',
      accessToken: this.appStore.userStore.token?.access
    }).then((res) => {
      return res?.filter(
        (entry) => entry.slug?.includes('vorhabenbeschreibung')
      )
    })
  }

  loadAggregationLayers = async () => {
    const url = `/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/eep/aggregation-layer/`
    await apiCall({
      url,
      method: 'GET',
      accessToken: this.appStore.userStore.token?.access
    }).then(
      action('fetchSuccess', (res) => {
        this.aggregationLayers = res

        if (!this.appStore.userCanViewBuildings) {
          this.selectedAggregationLayer = res[0]
        }
      })
    )
  }

  // Layer visibility/init logic
  get layers() {
    if (
      !this.appStore.appStore.selectedProject.uuid ||
      !this.appStore.userStore.token?.access ||
      !this.appStore.userStore.token?.time
    ) {
      return []
    }
    return [
      this.buildingLayerList.custom.visible && this.getCustomLayer(),
      this.buildingLayerList.geothermalLayer.init &&
        this.appStore.appStore.selectedProject &&
        this.getGeoThermalLayer(),
      this.getProjectLayer(),
      this.selectedAggregationLayer && this.getAggregationLayer(),
      this.buildingLayerList.vegetationsimulation.init &&
        this.getVegetationsimulationLayer(),
      this.buildingLayerList.heatPotential.visible &&
        this.getHeatPotentialBackgroundLayer(), // ¯\_(ツ)_/¯ see method comments
      this.getQuarterLayer(),
      this.getPolygonQuarterLayer(),
      this.buildingLayerList.drawNewQuarter.visible &&
        this.getInsertedQuarterLayer(),
      this.buildingLayerList.heatPotential.visible &&
        this.getHeatPotentialBorderLayer(), // ¯\_(ツ)_/¯ see method comments
      this.appStore.userCanViewBuildings && this.getBuildingsimulationLayer(),
      this.getConsumptionLayer(),
      this.buildingLayerList.solartile.init && this.getSolarTileLayer(),
      this.buildingLayerList.energyplant.init && this.getEnergyPlantLayer(),
      this.appStore.viewStore.selectedAddress && this.getAdressIconLayer(),
      this.appStore.userCanUseFilter && this.getFilterIconLayer()
    ]
  }

  // Layer Getter
  getCustomLayer() {
    return new WMSLayer({
      id: 'customLayer',
      maxZoom: 19,
      visible: this.buildingLayerList.custom.visible,
      tileLayerProps: {
        url: this.buildingLayerList.custom.url + '&',
        urlQueryStringParams: {
          version: this.buildingLayerList.custom.version
            ? this.buildingLayerList.custom.version
            : '1.3.0'
        }
      },
      updateTriggers: { getTileData: this.buildingLayerList.custom.url }
    })
  }

  getGeoThermalLayer(): WMSLayer | undefined {
    const wmsConfig = this.appStore.selectedProject.geothermalWMS
    if (wmsConfig === undefined) return
    const url = wmsConfig.serviceURL
    const delimiter = this.getUrlParameterDelimiter(url)

    return new WMSLayer({
      id: 'GeoThermalLayer',
      maxZoom: 12,
      visible: this.buildingLayerList.geothermalLayer.visible,
      tileLayerProps: {
        url: url + delimiter,
        urlQueryStringParams: {
          layers: wmsConfig.layer,
          version: wmsConfig.version
        }
      },
      updateTriggers: {
        getTileData: this.appStore.appStore.selectedProject.state
      }
    })
  }

  getAggregationLayer() {
    if (this.selectedAggregationLayer?.internal) {
      return
    }

    let themeAggregationLayer: any
    this.appStore.viewStore.selectedThemes?.forEach(
      (item) => (themeAggregationLayer = item.layer)
    )

    return this.getAggregationLayerForTheme(
      this.selectedAggregationLayer,
      themeAggregationLayer,
      'aggregationlayer'
    )
  }

  getAggregationLayerForTheme(
    selectedAggregationLayer,
    themeAggregationLayer,
    layerId
  ) {
    const selectedThemeAggregationLayer = themeAggregationLayer
      ? themeAggregationLayer.aggregationLayer
      : undefined

    const layerColors =
      themeAggregationLayer?.buildingsimulation?.color ||
      (themeAggregationLayer &&
      themeAggregationLayer[selectedThemeAggregationLayer] !== undefined
        ? themeAggregationLayer[selectedThemeAggregationLayer].color
        : undefined)

    const loadAggregationsFromString = (data: any, feature: string) => {
      if (data.content) {
        data.content[feature].properties.forEach((item: any) => {
          if (item.hasOwnProperty('aggregations_as_string')) {
            item.aggregations = JSON.parse(item.aggregations_as_string)
            delete item.aggregations_as_string
          }
        })
      }
    }

    const COLORS = {}

    if (
      selectedAggregationLayer.calculations?.[selectedThemeAggregationLayer]
        ?.legend
    ) {
      const legend =
        selectedAggregationLayer?.calculations[selectedThemeAggregationLayer]
          ?.legend
      Object.keys(legend).forEach(
        (key) => (COLORS[key] = [...legend[key].color, DEFAULT_COLOR_ALPHA])
      )
    } else if (layerColors) {
      Object.keys(layerColors).forEach(
        (key) => (COLORS[key] = [...layerColors[key], DEFAULT_COLOR_ALPHA])
      )
    }

    const props = {
      lineWidthMinPixels: selectedAggregationLayer.line_width,
      onTileLoad: (data: any) => {
        loadAggregationsFromString(data, 'lines')
        loadAggregationsFromString(data, 'points')
        loadAggregationsFromString(data, 'polygons')
      },
      getLineColor: (item: any) => {
        if (this.appStore.featureFocus.uuid === item.properties.uuid) {
          return (
            EnekaDefinitions.object.quarter.selection.linecolor ||
            UNDEFINED_LINE_COLOR
          )
        }
        if (
          selectedAggregationLayer.use_line_color &&
          item.properties.aggregations.hasOwnProperty(
            selectedThemeAggregationLayer
          )
        ) {
          return COLORS[
            item.properties.aggregations[selectedThemeAggregationLayer][
              PREDOMINANT_VALUE_FIELD
            ]
          ]
        }
        return UNDEFINED_LINE_COLOR
      },
      getFillColor: (item: any) => {
        if (this.appStore.featureFocus.uuid === item.properties.uuid) {
          return (
            EnekaDefinitions.object.quarter.selection.fillcolor ||
            UNDEFINED_FILL_COLOR
          )
        }

        if (
          item.properties.aggregations.hasOwnProperty(
            selectedThemeAggregationLayer
          )
        ) {
          return COLORS[
            item.properties.aggregations[selectedThemeAggregationLayer][
              PREDOMINANT_VALUE_FIELD
            ]
          ]
        }
        return UNDEFINED_FILL_COLOR
      },
      updateTriggers: {
        getFillColor: [
          selectedThemeAggregationLayer,
          selectedAggregationLayer,
          this.appStore.featureFocus
        ],
        getLineColor: [
          selectedThemeAggregationLayer,
          selectedAggregationLayer,
          this.appStore.featureFocus
        ]
      }
    }

    const url = `${process.env.REACT_APP_API_URL}/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/eep/aggregation-layer/mvt/{z}/{x}/{y}/?format=mvt&version=${this.appStore.buildingVersion}&layer-id=${selectedAggregationLayer.id}`

    return new AggregationLayer(props, {
      id: layerId,
      loadOptions: this.appStore.userStore.loadOptions,
      binary: true,
      maxZoom: 19,
      visible: true,
      data: url,
      pickable: true
    })
  }

  getProjectLayer() {
    return new GeoJsonLayer({
      id: 'ProjectArea',
      data: `${process.env.REACT_APP_API_URL}/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/?format=geojson`,
      loadOptions: this.appStore.userStore.loadOptions,
      opacity: 1,
      pickable: true,
      stroked: true,
      lineWidthMinPixels: 1,

      onDataLoad: action((d: any, ctx) => {
        const { viewport } = ctx.layer.context
        let obj = d.geometry.coordinates[0]
        if (obj.length === 1) {
          obj = obj[0]
        }
        const lngSorted = obj.map((e) => e[0]).sort((a, b) => a - b)
        const latSorted = obj.map((e) => e[1]).sort((a, b) => a - b)

        const viewportProps = viewport.fitBounds([
          [lngSorted[0], latSorted[0]],
          [lngSorted[lngSorted.length - 1], latSorted[latSorted.length - 1]]
        ])
        const { longitude, latitude, zoom } = viewportProps
        if (!this.appStore.viewStore.homePageViewState) {
          this.fitBounds = {
            longitude,
            latitude,
            zoom,
            uuid: this.appStore.appStore.selectedProject.uuid,
            bearing: 0,
            pitch: 0
          }
        }
      }),
      getLineColor: (e) => {
        return this.appStore.featureFocus.type === FeatureType.Project
          ? (EnekaDefinitions.object.project.selection.linecolor as RGBAColor)
          : (EnekaDefinitions.object.project.linecolor as RGBAColor)
      },
      getFillColor: EnekaDefinitions.object.project.fillcolor as RGBAColor,
      updateTriggers: {
        getLineColor: this.appStore.featureFocus
      }
    })
  }

  getVegetationsimulationLayer(): VegetationSimulationLayer {
    return new VegetationSimulationLayer({
      id: 'VegetationSimulationLayer-mvt',
      data: `${process.env.REACT_APP_API_URL}/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/vegetationsimulation/mvt/{z}/{x}/{y}/?format=mvt&version=${this.appStore.vegetationVersion}`,
      minZoom: 13,
      loadOptions: this.appStore.userStore.loadOptions,
      visible:
        this.buildingLayerList.vegetationsimulation.visible &&
        this.appStore.viewStore.viewState?.zoom > 9.95,
      pickable: true,
      updateTriggers: {
        getTileData: [this.buildingLayerList.vegetationsimulation.version],
        getFillColor: [
          this.appStore.focusType === FeatureType.Vegetation &&
            this.appStore.focusIds,
          this.buildingLayerList.vegetationsimulation.themeElement
        ],
        getLineColor: [
          this.buildingLayerList.vegetationsimulation.themeElement,
          this.appStore.featureFocus
        ]
      },
      getFillColor: (f: any) => {
        if (
          this.appStore.isFeatureInFocus(
            FeatureType.Vegetation,
            f.properties.uuid
          )
        )
          return EnekaDefinitions.object.building.selection.fillcolor
        return this.appStore.viewStore.handleColorShow(
          f.properties,
          this.buildingLayerList.vegetationsimulation.singleLayer,
          this.buildingLayerList.vegetationsimulation.themeElement
        )
      },
      getLineColor: (f: any) =>
        this.appStore.viewStore.getLineColorForObjects(
          f,
          FeatureType.Vegetation,
          this.buildingLayerList.vegetationsimulation,
          this.buildingLayerList.vegetationsimulation.visible
        )
    })
  }

  getQuarterLayer(): QuarterLayer {
    const layerId: string = 'MVTLayer-quarter-data'
    const queryParameterForLayer =
      this.appStore.filterStore.getQueryParameterGroupedByEndpoint(layerId, '&')
    const queryParameter =
      'quarter' in queryParameterForLayer ? queryParameterForLayer.quarter : ''

    return new QuarterLayer({
      id: layerId,
      data: `${process.env.REACT_APP_API_URL}/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/quarter/?format=geojson&time=${this.appStore.userStore.token?.time}&version=${this.appStore.quarterVersion}${queryParameter}`,
      pickable: true,
      loadOptions: this.appStore.userStore.loadOptions,
      visible: this.buildingLayerList.quarter.visible,
      getFillColor: (f: any) => {
        if (
          this.appStore.focusType === FeatureType.Quarter &&
          this.appStore.featureFocus.uuid === f.properties.uuid
        )
          return EnekaDefinitions.object.quarter.selection.fillcolor

        return this.appStore.viewStore.handleColorShow(
          f.properties,
          this.buildingLayerList.quarter.singleLayer,
          this.buildingLayerList.quarter.themeElement
        )
      },
      getLineColor: (f: any) =>
        this.appStore.focusType === FeatureType.Quarter &&
        this.appStore.featureFocus.uuid === f.properties.uuid
          ? EnekaDefinitions.object.quarter.selection.linecolor || [0, 0, 0, 0]
          : this.appStore.viewStore.handleLineColorShow(
              f.properties,
              this.buildingLayerList.quarter.singleLayer,
              this.buildingLayerList.quarter.themeElement
            ),

      updateTriggers: {
        getFillColor: [
          this.appStore.featureFocus,
          this.buildingLayerList.quarter.singleLayer
        ],
        getLineColor: this.appStore.featureFocus,
        data: [
          this.buildingLayerList.quarter.version,
          this.appStore.monitorStore?.monitorObject?.title,
          this.appStore.monitorStore?.currentObject?.[0]?.properties?.title,
          this.appStore.filterStore.filterParameter
        ]
      },

      textProps: {
        visible:
          this.buildingLayerList.quarter.visible &&
          this.appStore.viewStore.viewState?.zoom > 12.27,
        getBackgroundColor:
          this.themeTree.Allgemein.Quartiere.layer.quarter.color.labelText,
        updateTriggers: {
          visible:
            this.buildingLayerList.quarter.visible &&
            this.appStore.viewStore.viewState?.zoom > 12.27,
          data: [
            this.appStore.quarterVersion,
            this.appStore.monitorStore?.monitorObject?.title,
            this.appStore.monitorStore?.currentObject?.[0]?.properties?.title
          ]
        }
      }
    })
  }

  getPolygonQuarterLayer() {
    return new PolygonLayer({
      id: 'quarterLayer',
      data: [
        {
          contour: toJS(this.appStore.viewStore.quarterAreaPoints)
        }
      ],
      pickable: true,
      stroked: true,
      filled: true,
      wireframe: true,
      lineWidthMinPixels: 1,
      getPolygon: (d: any) => d.contour,
      getFillColor: EnekaDefinitions.object.quarter.fillcolor as RGBAColor,
      getLineColor: EnekaDefinitions.object.quarter.linecolor as RGBAColor,
      updateTriggers: {
        getFillColor: EnekaDefinitions.object.quarter.fillcolor,
        getLineColor: EnekaDefinitions.object.quarter.linecolor
      },
      getLineWidth: 1,
      opacity: 0.1,
      visible: this.buildingLayerList.drawQuarter.visible
    })
  }

  getInsertedQuarterLayer() {
    return new InsertedQuarterLayer({
      polygonProps: {
        visible: this.buildingLayerList.drawNewQuarter.visible,
        getFillColor: this.appStore.viewStore.hexToRgb(
          this.buildingLayerList.drawNewQuarter.color
        ),
        getLineColor: this.appStore.viewStore.hexToRgb(
          this.buildingLayerList.drawNewQuarter.color
        ),
        updateTriggers: {
          getFillColor: this.appStore.viewStore.hexToRgb(
            this.buildingLayerList.drawNewQuarter.color
          ),
          getLineColor: this.appStore.viewStore.hexToRgb(
            this.buildingLayerList.drawNewQuarter.color
          ),
          visible: this.buildingLayerList.drawNewQuarter.visible
        },
        data: [
          {
            contour: this.buildingLayerList.drawNewQuarter.data
              ? this.buildingLayerList.drawNewQuarter.data.geometry
                  .coordinates[0]
              : [],
            properties: this.buildingLayerList.drawNewQuarter.data?.properties
          }
        ]
      },
      textProps: {
        visible: this.buildingLayerList.drawNewQuarter.visible,
        data: [
          {
            coordinates: this.buildingLayerList.drawNewQuarter.data
              ? this.buildingLayerList.drawNewQuarter.data.geometry
                  .coordinates[0]
              : [],
            properties: this.buildingLayerList.drawNewQuarter.data?.properties
          }
        ],
        getBackgroundColor:
          this.themeTree.Allgemein.Quartiere.layer.quarter.color.labelText,
        updateTriggers: {
          visible: this.buildingLayerList.drawNewQuarter.visible
        }
      }
    })
  }

  getBuildingsimulationLayer() {
    const updateTriggers = [
      this.appStore.viewStore.themeString,
      this.appStore.focusType === FeatureType.Building &&
        this.appStore.focusIds,
      this.buildingLayerList.buildingsimulation.themeElement,
      this.buildingLayerList.buildingsimulation.visible,
      this.buildingLayerList.heatPotential.visible,
      this.selectedAggregationLayer,
      this.appStore.buildingVersion
    ]
    return new BuildingsimulationLayer({
      id: 'MVTLayer-buildingsimulation-data',
      dataUrl: `${process.env.REACT_APP_API_URL}/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/buildingsimulation/mvt/{z}/{x}/{y}.mvt?time=${this.appStore.userStore.token?.time}&version=${this.appStore.buildingVersion}`,
      pickable: true,
      onHover: (o: PickInfo<any>) => {
        this.setHoverInfo(o)
      },
      mvtProps: {
        loadOptions: this.appStore.userStore.loadOptions,
        tileSize: process.env.REACT_APP_TILE_SIZE,
        minZoom: 14,
        extruded: this.appStore.viewStore.building3dVision,
        material: true,
        getElevation: (d: any) => d.properties.height,
        opacity: 0.8,
        pickable: true,
        visible:
          !(
            this.appStore.layerStore.selectedAggregationLayer.id !== 'buildings'
          ) &&
          this.appStore.viewStore.viewState?.zoom >
            this.appStore.viewStore.getLayerVisibilityZoomValueByTheme(
              this.buildingLayerList.buildingsimulation?.themeElement
            ),
        getFillColor: (f: any) => {
          if (
            this.appStore.isFeatureInFocus(
              FeatureType.Building,
              f.properties.uuid
            )
          )
            return EnekaDefinitions.object.building.selection.fillcolor
          if (
            !this.buildingLayerList.buildingsimulation.visible &&
            this.selectedAggregationLayer.id === 'buildings'
          )
            return EnekaDefinitions.object.DummyLayer.default.fillcolor

          return this.appStore.viewStore.handleColorShow(
            f.properties,
            this.buildingLayerList.buildingsimulation.singleLayer,
            this.buildingLayerList.buildingsimulation.themeElement
          )
        },
        getLineColor: (f: any) => {
          if (
            this.appStore.isFeatureInFocus(
              FeatureType.Building,
              f.properties.uuid
            )
          )
            return EnekaDefinitions.object.building.selection.linecolor

          if (
            !this.buildingLayerList.buildingsimulation.visible &&
            this.selectedAggregationLayer.id === 'buildings'
          )
            return EnekaDefinitions.object.DummyLayer.default.linecolor

          return this.appStore.viewStore.handleColorShow(
            f.properties,
            this.buildingLayerList.buildingsimulation.singleLayer,
            this.buildingLayerList.buildingsimulation.themeElement
          )
        },
        updateTriggers: {
          loadOptions: this.appStore.userStore.token?.access,
          getTileData: [this.appStore.buildingVersion],
          getFillColor: updateTriggers,
          getLineColor: updateTriggers,
          extruded: this.appStore.viewStore.building3dVision
        }
      },
      iconProps: {
        iconAtlas,
        iconMapping: ICON_MAPPING,
        visible:
          this.buildingLayerList.marker.visible &&
          this.appStore.viewStore.viewState?.zoom > 13.2 &&
          this.selectedAggregationLayer.id === 'buildings',
        sizeScale:
          this.appStore.viewStore.viewState?.zoom >= 15.2
            ? 2.3
            : this.appStore.viewStore.viewState?.zoom >= 13.2
              ? 1
              : 0.5,
        getSize: () => {
          return this.appStore.viewStore.viewState?.zoom
        },
        getColor: (data: any) => {
          return this.buildingLayerList.marker.visible
            ? this.appStore.viewStore.handleColorShow(
                data.properties,
                this.buildingLayerList.marker.singleLayer,
                this.buildingLayerList.marker.themeElement
              )
            : [0, 0, 0]
        },
        getPosition: (d: any) => {
          const coords = d.geometry.coordinates[0]
          const latLng = [0, 0]
          const currentZoomLevel = this.appStore.viewStore.viewState?.zoom

          for (const coord of coords) {
            latLng[0] += coord[0]
            latLng[1] += coord[1]
          }
          return [
            latLng[0] / coords.length,
            latLng[1] / coords.length,
            (this.appStore.viewStore.building3dVision
              ? d.properties.height
              : 0) +
              (currentZoomLevel >= 17
                ? 3
                : currentZoomLevel >= 16
                  ? 10
                  : currentZoomLevel >= 14
                    ? 15
                    : 50)
          ]
        },
        updateTriggers: {
          getColor: [
            this.buildingLayerList.marker.themeElement,
            this.buildingLayerList.marker.visible
          ],
          getPosition: [
            this.appStore.viewStore.building3dVision,
            this.appStore.viewStore.viewState?.zoom
          ],
          data: this.buildingLayerList.marker.visible
        }
      },
      heatmapProps: {
        // TODO: getColorWeight für HexagonLayer
        getWeight: (d: any) => {
          const weight = this.buildingLayerList.heatmap.visible
            ? Math.round(
                this.handleWeight(
                  d.properties,
                  this.buildingLayerList.heatmap.themeElement.subTheme,
                  this.buildingLayerList.heatmap.apivariable
                )
              )
            : 0
          return isMobile && weight > 255 ? 255 : weight
        },
        colorDomain: this.appStore.viewStore.getColorDomainForHeatmap(
          this.buildingLayerList.heatmap
        ),
        aggregation: this.buildingLayerList.heatmap.aggregation,
        visible:
          this.buildingLayerList.heatmap.visible &&
          this.appStore.viewStore.viewState?.zoom <= 13.2 &&
          this.selectedAggregationLayer?.id === 'heatmap',
        colorRange: toJS(this.buildingLayerList.heatmap.color)
          ? Object.values(toJS(this.buildingLayerList.heatmap.color))
          : [
              [255, 255, 255],
              [0, 0, 0]
            ],
        updateTriggers: {
          getWeight: this.buildingLayerList.heatmap?.themeElement?.subTheme
        }
      }
    })
  }

  getConsumptionLayer() {
    return new ScatterplotLayer({
      id: 'consumptionLayer',
      data: toJS(this.appStore.viewStore.consumptionPoints),
      pickable: true,
      opacity: 1,
      stroked: true,
      filled: true,
      radiusScale: 5,
      radiusMinPixels: 5,
      radiusMaxPixels: 10,
      lineWidthMinPixels: 1,
      lineWidthMaxPixels: 1,
      getPosition: (d: any) => d.coordinates,
      getFillColor: [255, 140, 0],
      getLineColor: [0, 0, 0],
      visible: this.buildingLayerList.drawConsumption.visible
    })
  }

  getSolarTileLayer() {
    return new TileLayer({
      id: 'TileLayer-solartile-data',
      data: `${process.env.REACT_APP_API_URL}/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/solartile/{z}/{x}/{y}.png`,
      loadOptions: this.appStore.userStore.loadOptions,
      parameters: {
        depthTest: false
      },
      visible: this.buildingLayerList.solartile.visible,
      getTileData: async (tile) => {
        const data = await fetch(tile.url, {
          headers: {
            Authorization: 'Bearer ' + this.appStore.userStore.token?.access
          }
        })
          .then(async (res) => {
            if (res.status === 400) {
              return await fetch(res.url)
            }
            return res
          })
          .catch(() => {})

        return await load(data, ImageLoader, {
          image: { type: 'data' }
        }).then((image) => {
          const layerTypeString = this.appStore.viewStore.selectedThemes
            .filter((theme) => {
              switch (theme.subTheme) {
                case 'Solarstrahlung':
                case 'Solarthermiepotenzial':
                case 'Photovoltaikpotenzial':
                  return theme
                default:
                  return undefined
              }
            })
            .map((result) => {
              return result
            })
          const colorObject =
            EnekaDefinitions.themes[layerTypeString[0].singleTheme][
              layerTypeString[0].subTheme
            ].colorRange
          for (let i = 0; i < image?.data.length; i += 4) {
            const value =
              image.data[i + 0] * 256 +
              image.data[i + 1] +
              image.data[i + 2] / 256 -
              32768
            const color = colorObject.find((ele) => {
              return value <= ele.value
            })
            // const finalColor = Array.isArray(color.color) ? color.color : this.appStore.viewStore.hexToRgb(color.color);
            image.data[i + 0] = color?.color
              ? color.color[0]
              : Math.round((value / 1100) * 255) // R value
            image.data[i + 1] = color?.color
              ? color.color[1]
              : Math.round((value / 1100) * 255) // G value
            image.data[i + 2] = color?.color
              ? color.color[2]
              : Math.round((value / 1100) * 255) // B value
          }
          return image
        })
      },
      renderSubLayers: (props) => {
        const {
          bbox: { west, south, east, north }
        } = props.tile
        return [
          new BitmapLayer(props, {
            data: null,
            image: props.data,
            bounds: [west, south, east, north],
            defaultProps: {}
          })
        ]
      }
    })
  }

  getEnergyPlantLayer() {
    const sizeScale =
      this.appStore.viewStore.viewState?.zoom >= 15.2
        ? 2.3
        : this.appStore.viewStore.viewState?.zoom >= 13.2
          ? 1
          : 0.5

    const size = this.appStore.viewStore.viewState?.zoom
    return new EnergyPlantLayer({
      id: 'MVTLayer-energyplant-data',
      visible: this.buildingLayerList.energyplant.visible,
      binary: false,
      data: `${process.env.REACT_APP_API_URL}/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/energyplantsimulation/mvt/{z}/{x}/{y}/?format=mvt`,
      loadOptions: this.appStore.userStore.loadOptions,
      sizeScale,
      getSize: size,
      getPosition: (d) => d.geometry.coordinates,
      renderSubLayers: (props) => {
        return [
          new IconLayer(props, {
            pickable: true,
            id: props.id + 'background-icon',
            getIcon: () => {
              return {
                url: '/backgroundConsumptions.svg',
                height: 132,
                width: 132,
                anchorY: 132,
                mask: true
              }
            },
            getColor: (data) => {
              if (
                this.appStore.isFeatureInFocus(
                  FeatureType.EnergyPlant,
                  data.properties.uuid
                )
              )
                return EnekaDefinitions.object.energyplant.selection.linecolor // for now solid color without border

              return [139, 16, 20] // default energy plant color
            }
          }),
          new IconLayer(props, {
            id: props.id + 'type-icon',
            getIcon: (d: ObjectInfo) => {
              const title = this.appStore.plantTypes?.get(
                d.properties.plant_type_id
              )?.title
              const url =
                EnekaDefinitions.themes.Allgemein.Versorgungsanlagen.layer
                  .energyplant.consumptionType[title ?? '']

              return {
                url: url ?? '/plug.svg',
                height: 132,
                width: 132,
                anchorY: 132,
                mask: true
              }
            },
            getColor: [255, 255, 255, 254]
          })
        ]
      },
      updateTriggers: {
        getTileData: [this.appStore.energyPlantVersion],
        getIcon: [
          this.appStore.energyPlantVersion,
          this.appStore.plantTypes === undefined
        ],
        getColor: [this.appStore.energyPlantVersion, this.appStore.focusIds]
      }
    })
  }

  getAdressIconLayer(): IconLayer {
    const color: RGBAColor = [255, 0, 0]
    return new IconLayer({
      id: 'addressIcon',
      iconAtlas,
      iconMapping: ICON_MAPPING,
      visible: true,
      pickable: false,
      data: [this.appStore.viewStore.selectedAddress],
      getIcon: () => {
        return 'marker'
      },
      billboard: true,
      opacity: 1,
      getSize: () => 20,
      getColor: () => color,
      getPosition: (d) => d.coordinates,
      updateTriggers: this.appStore.viewStore.selectedAddress
    })
  }

  getFilterIconLayer(): IconLayer {
    const color: RGBAColor = [255, 0, 0]
    const size: number = 15
    const icon: string = 'marker'

    return new IconLayer({
      id: 'filterIconLayer',
      iconAtlas,
      iconMapping: ICON_MAPPING,
      visible: true,
      pickable: true,
      data: this.appStore.filterStore.searchResults,
      updateTriggers: this.appStore.filterStore.searchResults,
      billboard: true,
      opacity: 1,
      getIcon: () => icon,
      getSize: () => size,
      getColor: () => color,
      getPosition: (object, optional: any) => {
        const target = optional.target
        target[0] = optional.data.coordinates[optional.index * 2]
        target[1] = optional.data.coordinates[optional.index * 2 + 1]
        return target
      }
    })
  }

  // ¯\_(ツ)_/¯ we had to split the layers in background, heatmap and border layer
  // heatmap layer is implemented in buildingsimulation layer to avoid duplicated buildingsimulation requests
  // searching for a better solution
  getHeatPotentialBackgroundLayer(): GeoJsonLayer {
    return new GeoJsonLayer({
      data: `${process.env.REACT_APP_API_URL}/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/building/districtheating/`,
      id: 'districtHeatingLayer-background',
      loadOptions: this.appStore.userStore.loadOptions,
      parameters: {
        depthTest: false
      },
      material: false,
      getLineColor: [0, 0, 0, 0],
      getFillColor: EnekaDefinitions.themes.Wärme['Fern-/Nahwärmepotenzial']
        .layer.heatPotential.polygonColor.fill as RGBAColor
    })
  }

  // ¯\_(ツ)_/¯ we had to split the layers in background, heatmap and border layer
  // heatmap layer is implemented in buildingsimulation layer to avoid duplicated buildingsimulation requests
  // searching for a better solution
  getHeatPotentialBorderLayer(): GeoJsonLayer {
    return new GeoJsonLayer({
      data: `${process.env.REACT_APP_API_URL}/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/building/districtheating/`,
      id: 'districtHeatingLayer-border',
      loadOptions: this.appStore.userStore.loadOptions,
      parameters: {
        depthTest: false
      },
      getLineWidth: 4,
      material: false,
      getFillColor: [0, 0, 0, 0],
      getLineColor: EnekaDefinitions.themes.Wärme['Fern-/Nahwärmepotenzial']
        .layer.heatPotential.polygonColor.border as RGBAColor
    })
  }

  getUrlParameterDelimiter = (url: string) => {
    const urlsegments = url.split('/')
    const lastUrlSegment = urlsegments[urlsegments.length - 1]
    return lastUrlSegment.includes('?') ? '&' : '?'
  }
}

export default LayerStore
